import React, { useEffect, useState } from 'react';
import { debounce } from 'lodash';
import Label from './Label';
import useCheckoutState from '../../../hooks/useCheckoutState';
import emailSpellChecker from '../../../lib/emailSpellChecker';
import TextInput from '../../Form/components/TextInput/TextInput';
import StyledElement from '@repo/checkout/components/StyledElement';
import { useFocusContext } from '../../../contexts/FocusContext';
import FocusableInput from './FormField/FocusableInput';
import fieldAutoComplete from './FormField/fieldAutoComplete';
import clsx from 'clsx';
import getThemeLabels from '@repo/checkout/lib/getThemeLabels';

export default function EmailField({ field, onChange, onBlur }) {
  const { theme } = useCheckoutState();
  const labels = getThemeLabels(theme);
  const { label } = field;
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const focusContext = useFocusContext();

  // debounce validation
  const debouncedValidate = React.useCallback(
    debounce(emailInput => {
      const suggestion = emailSpellChecker(emailInput);

      if (suggestion) {
        setEmailSuggestion(suggestion);
      } else {
        setEmailSuggestion(null);
      }
    }, 500),
    [],
  );

  useEffect(() => {
    if (!field.value) return;

    debouncedValidate(field.value);
  }, [field.value, isFocused]);

  return (
    <>
      {label.length > 0 && (
        <StyledElement
          as={Label}
          elementPath="checkout.form.fields.label"
          htmlFor={field._id}
          className={clsx('cp_form-label')}
          required={field.required}
        >
          {label}
        </StyledElement>
      )}

      <FocusableInput elementId={field._id}>
        <StyledElement
          elementPath="checkout.form.fields.input"
          as={TextInput}
          id={field._id}
          name={field._id}
          type="email"
          autoComplete={fieldAutoComplete(field)}
          placeholder={field.placeholder || label}
          value={field.value}
          onChange={e => onChange(e.target.value)}
          required={field.required}
          className={clsx('cp_form-textinput')}
          onFocus={() => {
            setIsFocused(true);
            if (focusContext) {
              focusContext.setFocused(true, field._id);
            }
          }}
          onBlur={() => {
            setIsFocused(false);
            if (focusContext) {
              focusContext.setFocused(false, null);
            }
            onBlur();
          }}
        />
      </FocusableInput>

      {emailSuggestion && (
        <p className="cp_form-suggestion mt-2 [font-size:var(--cp_font-body-size-sm)] [line-height:var(--cp_font-body-line-height-sm)] text-right">
          {labels.emailSuggestionText}{' '}
          <button
            onClick={() => {
              onChange(emailSuggestion);
              setEmailSuggestion(null);
            }}
            className="cp_form-suggestion-button font-medium underline"
            type="button"
          >
            {emailSuggestion}
          </button>
          ?
        </p>
      )}
    </>
  );
}
